import React, { useContext } from 'react'
import CustTable from '../../Common/CustTable'
import { t } from 'i18next'
import { Button, Col, Popover, Row } from 'antd'
import { FormOutlined, UserAddOutlined, UserDeleteOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import { post } from '../../Common/api'
import GlobalContext from '../GlobalContext'
import StateTag from '../TagsPage/StateTag'
import AppointmentTypeTag from '../TagsPage/AppointmentTypeTag'

function AppointmentRangeWithNextAppointment ({ appointment, allAppointments }) {
  const rangeText = `${dayjs(appointment?.begin_date).format('H[h]mm')}-${dayjs(appointment?.end_date).format('H[h]mm')}`
  const nextAppointment = allAppointments
    .filter(s => s.box_id === appointment.box_id && dayjs(s.begin_date).isAfter(dayjs(appointment.begin_date)) && !s.is_cancel)
    .sort((a, b) => dayjs(a.begin_date) - dayjs(b.begin_date))[0]

  if (!nextAppointment) {
    return (
      <Popover title={t('Admin.AppointmentList.table.nextAppointmentEmpty')}>
        <span style={{ cursor: 'pointer' }}>{rangeText}</span>
      </Popover>
    )
  }

  const popoverContent = (
    <div style={{ width: '20em' }}>
      <Row><Col span={8}><strong>{t('Admin.AppointmentList.table.name')}</strong></Col><Col span={16}>{nextAppointment.name}</Col></Row>
      <Row><Col span={8}><strong>{t('Admin.AppointmentList.table.range')}</strong></Col><Col span={16}>{dayjs(nextAppointment.begin_date).format('HH[h]mm')}</Col></Row>
      <Row><Col span={8}><strong>{t('Admin.AppointmentList.table.phone')}</strong></Col><Col span={16}>+{nextAppointment.phone}</Col></Row>
      <Row style={{ marginTop: '0.5em' }}><Col span={24}><Link to={`/admin/appointments/${nextAppointment.id}?from=list`}><Button size="small" icon={<FormOutlined />}>{t('Admin.AppointmentList.table.viewAppointment')}</Button></Link></Col></Row>
    </div>
  )

  return (
    <Popover content={popoverContent} title={t('Admin.AppointmentList.table.nextAppointment')}>
      <span style={{ cursor: 'pointer' }}>{rangeText}</span>
    </Popover>
  )
}

function historyUrl (appointment) {
  return `/admin/appointments_history?last_name=${appointment.last_name}&first_name=${appointment.first_name}&period=range`
}

export default function AppointmentList ({ filteredAppointments }) {
  const screens = useBreakpoint()
  const { user, notificationApi, appointments, boxes, appointmentsDate, updateAppointments } = useContext(GlobalContext)
  const dayAppointments = appointments.filter((appointment) => dayjs(appointment.begin_date).format('YYYY-MM-DD') === appointmentsDate.format('YYYY-MM-DD'))

  const assign = (id) => {
    post({
      url: `admin/appointments/${id}/assign`,
      params: {},
      notification: { api: notificationApi, state: 'update' },
      onSuccess: (json) => {
        updateAppointments([json])
      },
      onFailure: () => {
      }
    })
  }

  const unassign = (id) => {
    post({
      url: `admin/appointments/${id}/unassign`,
      params: {},
      notification: { api: notificationApi, state: 'update' },
      onSuccess: (json) => {
        updateAppointments([json])
      },
      onFailure: () => {
      }
    })
  }

  const columns = (screens, boxes) => [
    {
      title: t('Admin.AppointmentList.table.name'),
      dataIndex: 'name',
      key: 'name',
      width: 190,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (v, o) => <Link to={historyUrl(o)} >{v}</Link>,
      showSorterTooltip: false
    },
    {
      title: t('Admin.AppointmentList.table.age'),
      dataIndex: 'age',
      key: 'age',
      width: 40
    },
    {
      title: t('Admin.AppointmentList.table.bookedAppointments'),
      dataIndex: 'booked_appointments',
      key: 'booked_appointments',
      width: 80,
      render: (_, o) => o.booked_appointments
    },
    {
      title: t('Admin.AppointmentList.table.box'),
      dataIndex: 'box.name',
      key: 'box.name',
      render: (_, o) => {
        const box = boxes.find(box => box.id === o.box_id)
        return `${box.name} - ${box.interphone}`
      },
      width: 200,
      sorter: (a, b) => {
        const boxA = boxes.find(box => box.id === a.box_id)
        const boxB = boxes.find(box => box.id === b.box_id)
        return boxA.name.localeCompare(boxB.name)
      },
      showSorterTooltip: false
    },
    {
      title: t('Admin.AppointmentList.table.assigned'),
      dataIndex: 'assigned_short_name',
      key: 'assigned_short_name',
      width: 100,
      sorter: (a, b) => a.assigned_short_name.localeCompare(b.assigned_short_name),
      showSorterTooltip: false,
      render: (_, o) => o.assigned_short_name
    },
    {
      width: 80,
      title: t('Admin.AppointmentList.table.appointmentType'),
      dataIndex: 'type',
      key: 'type',
      render: (_, o) => <AppointmentTypeTag appointment={o} />,
      sorter: (a, b) => a?.type.localeCompare(b?.type),
      showSorterTooltip: false,
      hidden: !screens.md
    },
    {
      width: 100,
      title: t('Admin.AppointmentList.table.range'),
      dataIndex: 'range',
      key: 'range',
      render: (_, o) => <AppointmentRangeWithNextAppointment appointment={o} allAppointments={dayAppointments} />,
      sorter: (a, b) => dayjs(a.begin_date) - dayjs(b.begin_date),
      showSorterTooltip: false
    },
    {
      width: 100,
      title: t('Admin.AppointmentList.table.phone'),
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => `+${phone}`,
      sorter: (a, b) => a.phone - b.phone,
      showSorterTooltip: false
    },
    {
      width: 160,
      title: t('Admin.AppointmentList.table.state'),
      dataIndex: 'state',
      key: 'state',
      render: (_, o) => <StateTag appointment={o} boxes={boxes} />,
      sorter: (a, b) => a?.state.localeCompare(b?.state),
      showSorterTooltip: false
    },
    {
      width: 70,
      title: t('Admin.AppointmentList.table.digicode'),
      dataIndex: 'digicode',
      key: 'digicode',
      sorter: (a, b) => a?.digicode.localeCompare(b?.digicode),
      showSorterTooltip: false,
      hidden: !screens.md
    },
    {
      width: 80,
      key: 'action',
      render: (_, o) => <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <Link to={`/admin/appointments/${o.id}?from=list`}><Button shape="circle" icon={<FormOutlined />} /></Link>
        { o.assigned_id !== user.id &&
          <Button shape="circle" icon={<UserAddOutlined />} onClick={() => assign(o.id)} />
        }
        { o.assigned_id === user.id &&
          <Button shape="circle" icon={<UserDeleteOutlined />} onClick={() => unassign(o.id)} />
        }
      </div>
    }
  ].filter((col) => !col.hidden)

  return (
    <>
      {
        boxes && <CustTable
          height={660}
          initColumns={columns(screens, boxes)}
          dataSource={filteredAppointments}
          rowClassName={(appointment, _) => appointment.state === 'done' && !appointment.feedback_done ? 'highlight' : '' }
        />
      }
    </>
  )
}
